import React, { useState, useEffect } from 'react';
import axios from '../lib/axios';
import { 
  Bell, BellOff, MessageSquare, Gavel, 
  RefreshCw, Loader2, Send, Smartphone, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { API } from '../App';

const NotificationSettings = ({ user, onClose }) => {
  // Raw preferences from DB (10 booleans)
  const [preferences, setPreferences] = useState({
    push_enabled: true,
    messages: true,
    offers: true,
    outbid: true,
    auction_won: true,
    sale: true,
    reviews: true,
    system: true,
    favorites: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSend, setTestingSend] = useState(false);
  
  const {
    permission,
    isSupported,
    isSubscribed,
    isLoading: pushLoading,
    error: pushError,
    iosInfo,
    subscribe,
    unsubscribe
  } = usePushNotifications(user);

  // Load preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await axios.get(`${API}/users/notification-preferences`);
        setPreferences(response.data);
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      loadPreferences();
    }
  }, [user]);

  // Compute grouped states from raw preferences
  const groupedStates = {
    berichten: preferences.messages && preferences.offers,
    veilingen: preferences.outbid && preferences.auction_won && preferences.sale,
    updates: preferences.reviews && preferences.favorites && preferences.system
  };

  // Save preferences to backend
  const savePreferences = async (newPrefs) => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/notification-preferences`, newPrefs);
      setPreferences(newPrefs);
    } catch (error) {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  // Toggle a group (maps to multiple DB fields)
  const toggleGroup = (groupKey) => {
    const newValue = !groupedStates[groupKey];
    let updates = {};
    
    switch (groupKey) {
      case 'berichten':
        updates = { messages: newValue, offers: newValue };
        break;
      case 'veilingen':
        updates = { outbid: newValue, auction_won: newValue, sale: newValue };
        break;
      case 'updates':
        updates = { reviews: newValue, favorites: newValue, system: newValue };
        break;
      default:
        return;
    }
    
    const newPrefs = { ...preferences, ...updates };
    savePreferences(newPrefs);
  };

  // Handle push notification toggle
  const handlePushToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success('Push notificaties uitgeschakeld');
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success('Push notificaties ingeschakeld');
      } else if (permission === 'denied') {
        toast.error('Schakel notificaties in via je browserinstellingen');
      }
    }
  };

  // Send test notification
  const sendTestNotification = async () => {
    setTestingSend(true);
    try {
      const response = await axios.post(`${API}/push/test`);
      if (response.data.success) {
        toast.success('Test notificatie verzonden!');
      } else {
        toast.error(response.data.message || 'Verzenden mislukt');
      }
    } catch (error) {
      toast.error('Verzenden mislukt');
    } finally {
      setTestingSend(false);
    }
  };

  // Notification groups config with detailed descriptions
  const notificationGroups = [
    { 
      key: 'berichten', 
      label: 'Berichten & Biedingen', 
      icon: MessageSquare, 
      description: 'Nieuwe berichten, biedingen op je advertenties, ingetrokken biedingen en ruil-updates',
      includes: ['Chatberichten', 'Nieuwe biedingen', 'Bod ingetrokken', 'Ruil geaccepteerd/afgewezen'],
      color: 'text-blue-500'
    },
    { 
      key: 'veilingen', 
      label: 'Veilingen & Verkoop', 
      icon: Gavel, 
      description: 'Overboden meldingen, gewonnen veilingen en verkoop notificaties',
      includes: ['Overboden op veiling', 'Veiling gewonnen', 'Item verkocht'],
      color: 'text-orange-500'
    },
    { 
      key: 'updates', 
      label: 'Favorieten & Updates', 
      icon: RefreshCw, 
      description: 'Prijswijzigingen op favorieten en systeem meldingen',
      includes: ['Favoriet: prijswijziging', 'Favoriet: verkocht/gereserveerd', 'Systeem updates'],
      color: 'text-green-500'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Push Notifications Master Card */}
      <div className="p-5 bg-card rounded-2xl border border-border">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${isSubscribed ? 'bg-primary/10' : 'bg-muted'}`}>
              {isSubscribed ? (
                <Bell className="w-5 h-5 text-primary" />
              ) : (
                <BellOff className="w-5 h-5 text-muted-foreground" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Push Notificaties</h3>
              <p className="text-sm text-muted-foreground">
                {isSubscribed ? 'Actief op dit apparaat' : 'Ontvang real-time meldingen'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pushLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            <Switch 
              checked={isSubscribed}
              onCheckedChange={handlePushToggle}
              disabled={!isSupported || pushLoading}
              data-testid="push-notifications-toggle"
            />
          </div>
        </div>
        
        {/* Permission Denied Warning */}
        {permission === 'denied' && (
          <div className="mt-4 p-3 bg-amber-500/10 rounded-xl flex items-start gap-3">
            <ExternalLink className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Schakel notificaties in je browserinstellingen in om updates te ontvangen.
            </p>
          </div>
        )}
        
        {/* Push Error - Edge specific help */}
        {pushError && pushError.includes('push service error') && (
          <div className="mt-4 p-3 bg-red-500/10 rounded-xl">
            <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">
              Push registratie mislukt
            </p>
            <p className="text-xs text-muted-foreground mb-2">
              Dit kan komen door browser cache. Probeer deze stappen:
            </p>
            <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Ga naar browser instellingen → Privacy → Site instellingen</li>
              <li>Zoek deze site en verwijder alle data</li>
              <li>Ververs de pagina (Ctrl+Shift+R)</li>
              <li>Probeer opnieuw</li>
            </ol>
          </div>
        )}
        
        {/* iOS PWA Notice */}
        {iosInfo?.needsPWA && (
          <div className="mt-4 p-3 bg-blue-500/10 rounded-xl flex items-start gap-3">
            <Smartphone className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-600 dark:text-blue-400">
              <p className="font-medium mb-1">Installeer de app voor push notificaties</p>
              <p className="text-muted-foreground">
                Op iPhone werken push notificaties alleen vanuit de geïnstalleerde app.
              </p>
            </div>
          </div>
        )}
        
        {/* Not Supported Notice */}
        {!isSupported && !iosInfo?.needsPWA && (
          <div className="mt-4 p-3 bg-muted rounded-xl">
            <p className="text-sm text-muted-foreground">
              Push notificaties worden niet ondersteund in deze browser.
            </p>
          </div>
        )}
        
        {/* Test Button - Only when subscribed */}
        {isSubscribed && (
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Test of notificaties werken
            </p>
            <button
              onClick={sendTestNotification}
              disabled={testingSend}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              data-testid="test-push-notification-btn"
            >
              {testingSend ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Test
            </button>
          </div>
        )}
      </div>

      {/* Notification Type Groups - Clean list */}
      <div className="p-5 bg-card rounded-2xl border border-border">
        <h3 className="font-semibold text-foreground mb-4">Notificatie Types</h3>
        
        <div className="space-y-1">
          {notificationGroups.map((group, index) => {
            const IconComponent = group.icon;
            return (
              <div key={group.key}>
                <div className="flex items-start justify-between py-3">
                  <div className="flex items-start gap-3 flex-1 mr-3">
                    <IconComponent className={`w-5 h-5 ${group.color} mt-0.5 flex-shrink-0`} />
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-foreground">{group.label}</p>
                      <p className="text-xs text-muted-foreground mb-1.5">{group.description}</p>
                      {group.includes && (
                        <div className="flex flex-wrap gap-1">
                          {group.includes.map((item, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-secondary rounded text-muted-foreground">
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <Switch 
                    checked={groupedStates[group.key]}
                    onCheckedChange={() => toggleGroup(group.key)}
                    disabled={saving}
                    className="flex-shrink-0"
                    data-testid={`notification-toggle-${group.key}`}
                  />
                </div>
                {index < notificationGroups.length - 1 && (
                  <div className="border-t border-border/50" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Saving indicator */}
      {saving && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Opslaan...
        </div>
      )}

      {/* Close button */}
      {onClose && (
        <button 
          onClick={onClose}
          className="w-full py-2.5 bg-secondary hover:bg-secondary/80 text-foreground font-medium rounded-xl transition-colors"
          data-testid="close-notification-settings"
        >
          Klaar
        </button>
      )}
    </div>
  );
};

export default NotificationSettings;
